import { FC } from "react";

import { FormBackgroundTheme, parseFormBackgroundTheme, resolveBackgroundThemeCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveFormEditorStepColorStateless, StepColorFormData } from "./LiveFormEditorStepColor.stateless";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { getColorChangedEventProperties } from "@/components/LiveEditorElements/liveEditorColors";

export const DonationLiveEditorStepColor: FC = () => {
  const {
    apiFormObject: apiDonationForm,
    optionalFundraiserToken,
    featureFlags: {
      isDelightfulPagesActive,
      isDelightfulPagesLoading,
      isCustomColorTileActive,
      isCustomColorTileLoading,
    },
  } = useLiveFormEditorContext<"donation-form">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { t } = useTranslate();

  const currentBackgroundTheme = parseFormBackgroundTheme(apiDonationForm.backgroundTheme);

  const defaultValues: StepColorFormData = {
    color: apiDonationForm.color,
    isDarkMode: Boolean(apiDonationForm.isDarkMode),
    backgroundTheme: currentBackgroundTheme,
    colorToShowInPreview: apiDonationForm.color,
  };
  const form = useForm<StepColorFormData>({ defaultValues });

  const { mutateAsync: updateVisualOptions } = trpc.updateDonationFormVisualOptions.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepColorFormData>({
    type: "donation-form",
    form,
    saveData: async (formData) => {
      return updateVisualOptions({
        donationFormId: apiDonationForm.id,
        color: formData.color,
        isDarkMode: formData.isDarkMode,
        backgroundTheme: formData.backgroundTheme,
        fundraiserToken: optionalFundraiserToken,
      });
    },
    errorMessage: t("dashboard", "liveFormEditor.stepColor.error"),
    getSubmitTrackingProperties: (formData) => ({
      ...getColorChangedEventProperties({
        color: formData.color,
        colorOnStepArrival: defaultValues.color,
        editorType: "donation_form",
      }),
      is_dark_mode: formData.isDarkMode,
      background_theme: formData.backgroundTheme,
      background_category: resolveBackgroundThemeCategory(formData.backgroundTheme),
      is_delightful_pages_active: isDelightfulPagesActive,
    }),
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.donation.color = formData.colorToShowInPreview;
      liveDataToMutate.donation.isDarkMode = formData.isDarkMode;
      liveDataToMutate.donation.backgroundTheme = formData.backgroundTheme;
    },
  });

  const handleTrackOnColorChange = (color: string) =>
    logUserActionEvent(
      AmplitudeEvents.FormColorChanged,
      getColorChangedEventProperties({ color, colorOnStepArrival: defaultValues.color, editorType: "donation_form" })
    );

  const handleTrackOnCustomColorCancel = () =>
    logUserActionEvent(AmplitudeEvents.FormCustomColorCancelled, { editor_type: "donation_form" });

  const handleTrackOnBackgroundStyleChange = (theme: FormBackgroundTheme | null, category: string) =>
    logUserActionEvent(AmplitudeEvents.FormBackgroundStyleChanged, {
      background_theme: theme,
      background_category: category,
    });

  const handleTrackOnDarkModeChange = (isDarkMode: boolean) =>
    logUserActionEvent(AmplitudeEvents.FormDarkModeChanged, { is_dark_mode: isDarkMode });

  return (
    <LiveFormEditorStepColorStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      trackOnColorChange={handleTrackOnColorChange}
      trackOnCustomColorCancel={handleTrackOnCustomColorCancel}
      trackOnBackgroundStyleChange={handleTrackOnBackgroundStyleChange}
      trackOnDarkModeChange={handleTrackOnDarkModeChange}
      isDelightfulPagesActive={isDelightfulPagesActive}
      isDelightfulPagesLoading={isDelightfulPagesLoading}
      isCustomColorTileActive={isCustomColorTileActive}
      isCustomColorTileLoading={isCustomColorTileLoading}
    />
  );
};
